/**
 * The field descriptors and the pure form-value helpers behind `VocabularyTab`.
 *
 * These live outside the component file so they can be imported and tested
 * without pulling in a React render, and so exporting them does not break the
 * component file's fast-refresh boundary. `VocabularyTab` re-exports the two
 * types, so existing importers are unaffected.
 */

export type VocabFieldType =
  | 'text' | 'number' | 'boolean' | 'json'
  | 'intArray' | 'stringArray' | 'jsonArray'
  | { select: Array<{ value: string; label: string }> };

export type VocabField<T> = {
  key: keyof T & string;
  label: string;
  type: VocabFieldType;
  required?: boolean;
  hint?: string;
  /**
   * Value used when ADDING a row, before the user touches the field. Without
   * it a boolean initialises to '' and parses to false — which for a field
   * meaning "validate this charge" is the one default nobody would pick on
   * purpose. Absent means the existing behaviour, so fields that do not set it
   * are unaffected.
   */
  default?: unknown;
};

/** Form state is all strings; this is the encoding into that. */
export function toFormValue(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) {
    // An array of OBJECTS — bv_times_mapping.windows — must round-trip as JSON.
    // join(', ') would render it "[object Object]" and the edit form would save
    // that back over a valid window list.
    return v.some((x) => x !== null && typeof x === 'object')
      ? JSON.stringify(v)
      : v.join(', ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * The form values a NEW row starts with: a field's `default` where it sets one,
 * '' otherwise — which is exactly the previous behaviour for every field that
 * does not.
 *
 * This is deliberately used ONLY on the add path. `VocabularyTab`'s `openEdit`
 * must keep reading the stored row, including a stored `false`, or editing a row
 * would silently flip it back to the default.
 */
export function initialFormFor<T>(fields: Array<VocabField<T>>): Record<string, string> {
  return Object.fromEntries(
    fields.map((f) => [f.key, f.default === undefined ? '' : toFormValue(f.default)]),
  );
}
